/* eslint-disable @typescript-eslint/no-explicit-any */
import { FetchError, $fetch, type FetchOptions } from 'ofetch';
import { ValidationError } from '~/error/ValidationError';
import { UnAuthorizedError } from '~/error/UnAuthorizedError';
import { ForbiddenError } from '~/error/ForbiddenError';
import { NotFoundError } from '~/error/NotFoundError';
import { ApiClientError } from '~/error/ApiClientError';
import { ApiServerError } from '~/error/ApiServerError';
import type { ApiResponse } from '~/types/api/common/apiResponse';
import type { RequestOptions } from '~/types/api/common/requestOptions';
import { ClientError } from '~/error/ClientError';
import type { GetCsrfTokenResponse } from '~/types/api/auth/getCsrfToken';
import { MessageCodeEnum } from '~/constants/MessageCode';
import { UnexpectedResponseError } from '~/error/UnexpectedResponseError';

/**
 * PlatAPIを呼び出すためのクライアント
 */
export class ApiClient {
  /** 画面ID */
  private displayId: string;
  /** デフォルトのリクエストオプション */
  private defaultOptions: RequestOptions;
  /** CSRFトークン取得エンドポイント */
  private readonly CSRF_ENDPOINT = '/auth/csrf';
  /** CSRF保護が不要なエンドポイントのリスト */
  private readonly CSRF_EXEMPT_ENDPOINTS = ['session', 'login', 'csrf', 'logout', 'public'];

  constructor(displayId: string, defaultOptions: RequestOptions = {}) {
    this.defaultOptions = defaultOptions;
    this.displayId = displayId;
  }

  async get<T>(
    endpoint: string,
    query?: URLSearchParams,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    let urlWithQuery = endpoint;
    if (query?.toString()) {
      urlWithQuery += '?' + query.toString();
    }
    return this.request<ApiResponse<T>>('GET', urlWithQuery, undefined, options);
  }

  async post<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<ApiResponse<T>>('POST', endpoint, data, options);
  }

  async put<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<ApiResponse<T>>('PUT', endpoint, data, options);
  }

  async delete<T>(
    endpoint: string,
    query?: URLSearchParams,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    let urlWithQuery = endpoint;
    if (query?.toString()) {
      urlWithQuery += '?' + query.toString();
    }
    return this.request<ApiResponse<T>>('DELETE', urlWithQuery, undefined, options);
  }

  async getFile(endpoint: string, query?: URLSearchParams, options?: RequestOptions): Promise<Blob> {
    let urlWithQuery = endpoint;
    if (query?.toString()) {
      urlWithQuery += '?' + query.toString();
    }
    return this.request<Blob>('GET', urlWithQuery, undefined, options);
  }

  async getFilePost(endpoint: string, data?: any, options?: RequestOptions): Promise<Blob> {
    // CSRF保護が必要な場合、トークンを取得してヘッダーに設定
    const headers: HeadersInit = {
      ...this.defaultOptions.headers,
      ...options?.headers,
    };
    const { csrfToken, headerName } = await this.fetchCsrfToken();

    if (csrfToken && headerName) {
      (headers as Record<string, string>)[headerName] = csrfToken;
    }

    return this.request<Blob>('POST', endpoint, data, {
      ...options,
      headers,
    });
  }

  private async request<T>(
    method: string,
    endpoint: string,
    data?: any,
    options?: RequestOptions,
  ): Promise<T> {
    const { apiBaseUrl, apiRequestTimeout } = useNuxtApp().$config.public;
    const headers: HeadersInit = {
      ...this.defaultOptions.headers,
      ...options?.headers,
    };

    // CSRF保護が必要な場合、トークンを取得してヘッダーに設定
    if (this.needsCsrfProtection(method, endpoint)) {
      const { csrfToken, headerName } = await this.fetchCsrfToken();

      if (csrfToken && headerName) {
        (headers as Record<string, string>)[headerName] = csrfToken;
      }
    }

    const fetchOptions: FetchOptions<'json', any> = {
      method,
      baseURL: apiBaseUrl,
      timeout: options?.timeout ?? Number(apiRequestTimeout),
      credentials: 'include', // Cookieをリクエストに含める
      headers,
      body: data ? JSON.stringify(data) : undefined,
      cache: options?.cache || this.defaultOptions.cache,
    };

    try {
      const logger = useLogger(this.displayId);
      logger.info('APIリクエスト', {
        endpoint,
        body: data,
      });
      const response = await $fetch<T>(endpoint, fetchOptions);
      logger.info('APIレスポンス', {
        endpoint,
        body: response,
      });
      return response;
    }
    catch (error) {
      throw this.wrapError(error);
    }
  }

  /**
   * エラーオブジェクトの内容からエラーの種類を特定し、それに応じた例外クラスを生成して返却する。
   *
   * @param error
   */
  private wrapError(error: any) {
    if (error instanceof FetchError) {
      const statusCode = error.statusCode ?? 0;
      // ネットワークエラー
      if (!error.response) {
        return new ClientError(MessageCodeEnum.NETWORK_ERROR, this.displayId);
      }
      // ネットワークエラーではないが、レスポンスデータが無い場合は500エラー扱いとする
      if (!error.response._data) {
        return new ClientError(MessageCodeEnum.UNKNOWN_ERROR, this.displayId);
      }

      // WAFに弾かれた場合などレスポンスデータが想定するJSON形式でない場合は、
      // HTTPステータスコードによらず「予期せぬエラー」として扱う
      const data = error.response._data;
      if (
        typeof data !== 'object'
        || typeof data.status !== 'string'
        || typeof data.message !== 'string'
      ) {
        const responseSnippet
          = (typeof data === 'string' ? data : JSON.stringify(data) ?? '').slice(0, 200);
        useLogger(this.displayId).warn('APIレスポンスが想定する形式ではありません', undefined, {
          statusCode,
          responseSnippet,
        });
        return new UnexpectedResponseError(statusCode, responseSnippet, this.displayId);
      }

      const status = data.status as string;
      const message = data.message as string;
      const errorId = data.errorId as string;
      const validationErrors = data.validationErrors as string[];
      // バリデーションエラー
      if (statusCode === 400 && !!validationErrors) {
        return new ValidationError(status, message, errorId, this.displayId, validationErrors);
      }
      // 認証エラー
      else if (statusCode === 401) {
        return new UnAuthorizedError(status, message, errorId, this.displayId);
      }
      // 権限エラー
      else if (statusCode === 403) {
        return new ForbiddenError(status, message, errorId, this.displayId);
      }
      // リソース無しエラー
      else if (statusCode === 404) {
        return new NotFoundError(status, message, errorId, this.displayId);
      }
      // その他400エラー
      else if (statusCode >= 400 && statusCode < 500) {
        return new ApiClientError(status, message, errorId, this.displayId);
      }
      // 上記以外はサーバーエラーとする
      else {
        return new ApiServerError(status, message, errorId, this.displayId);
      }
    }

    // FetchErrorでない場合はそのまま返却する
    return error;
  }

  /**
   * CSRFトークンを取得し、ヘッダー名とトークン値を返す
   *
   * @returns csrfToken: CSRFトークン値, headerName: CSRFトークンを設定するヘッダー名
   */
  private async fetchCsrfToken(): Promise<{
    csrfToken: string | undefined;
    headerName: string | undefined;
  }> {
    try {
      const { apiBaseUrl } = useNuxtApp().$config.public;
      const response = await $fetch<ApiResponse<GetCsrfTokenResponse>>(
        `${apiBaseUrl}${this.CSRF_ENDPOINT}`,
        {
          method: 'GET',
          credentials: 'include',
        },
      );

      const headerName = response.data.headerName;
      const csrfToken = useCookie('XSRF-TOKEN').value ?? undefined;

      return { csrfToken, headerName };
    }
    catch (error) {
      const logger = useLogger(this.displayId);
      logger.error('CSRFトークンの取得に失敗しました', error as Error);
      return { csrfToken: undefined, headerName: undefined };
    }
  }

  /**
   * エンドポイントがCSRF保護の対象かどうかを判定
   *
   * @param method HTTPメソッド
   * @param endpoint エンドポイントパス
   * @returns CSRF保護が必要な場合はtrue
   */
  private needsCsrfProtection(method: string, endpoint: string): boolean {
    if (method === 'GET') {
      return false;
    }

    return !this.CSRF_EXEMPT_ENDPOINTS.some((exempt) => endpoint.includes(exempt));
  }
}
